import React, { useCallback } from "react";
import { IDMobile } from "../../../components/IDs/IDs";
import type {
  IQuotesAPIResponse,
  IQuoteRequest,
  StatusAndId,
  QuoteStatus,
  User,
  QuoteRequestItem,
} from "../../../types/types";
import { PageWrapper } from "../../../layout/portalPageLayout";
import {
  SellerQuoteStatusFilters,
  getQuoteProductsAsText,
  SellerQuoteStatus,
  getQuoteItemsList,
} from "../../../components/QuoteStatus";
import {
  formatDateTime,
  useInAppNotifications,
  useStoreState,
  rowHover,
  DEFAULT_PER_PAGE,
} from "../../../util/util";
import { useRoutePath } from "../../../util/Routing";
import { useAuthContext } from "../../../components/Auth";
import ReactTooltip from "react-tooltip";
import { useTranslation } from "react-i18next";
import { SellerCreateQuote } from "../../Seller/SellerCreateQuote/SellerCreateQuote";
import { Modal } from "../../../components/Modal/Modal";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import { calculatePriceDetails } from "../../../components/PriceDetails/PriceDetails";
import {
  TooltipCell,
  update_status_string_list,
} from "../../Buyer/BuyerOrders/BuyerOrders.util";
import { TrimmedName } from "../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import type { ColumnDef } from "@tanstack/react-table";
import {
  getAccountManagerCSM,
  useAccountManagersCSMFilters,
} from "../SellerOrdersListPage/util";
import { useListPage } from "../shared";
import type { TFunction } from "react-i18next";
import { TablePageLayout } from "../../../components/TablePage/TablePageLayout";
import { FiltersDropDown } from "../../../components/FiltersDropDown/FiltersDropDown";
import {
  PrimaryButtonWithPlusIcon,
  SecondaryButtonLargeV2,
} from "../../../components/Buttons/Buttons";
import { SlideOut } from "../../../components/SlideOut/SlideOut";
import { DownloadReport } from "../SellerOrdersListPage/DownloadReport/DownloadReport";

const localStrings = (t: TFunction) => ({
  pageTitle: t("Quotes"),
  sellerSearchBarPlaceholder: t("Search by Product, Customers or Quote ID"),
  buyerSearchBarPlaceholder: t("Search by Product or Quote ID"),
  status: t("Status"),
  perPage: t("Per Page"),
});

type QuotesTable = {
  id: string;
  products: { items: QuoteRequestItem[]; id: string };
  from: string;
  total_value: string;
  last_activity: string;
  status: StatusAndId<QuoteStatus>;
  unread: boolean;
  unread_message_count?: number;
  account_manager_csm: string;
};

/**
 * Shared page component for the seller "Quotes" page.
 * The page displays a list of quote requests.
 */
export const SellerQuotesListPage = () => {
  const { t } = useTranslation();
  const { user, roleIsSomeKindOfSeller } = useAuthContext();
  const { storefront_id } = useStoreState();
  const { notifications } = useInAppNotifications(storefront_id, user as User);
  const { accountPath } = useRoutePath();

  const { accountManagers, customerServiceReps } =
    useAccountManagersCSMFilters("quotes");

  // Helper functions
  const getSearchPlaceholder = useCallback(() => {
    if (user?.rbac_role === "seller_admin") {
      return localStrings(t).sellerSearchBarPlaceholder;
    }
    return localStrings(t).buyerSearchBarPlaceholder;
  }, [t, user?.rbac_role]);

  const columns: ColumnDef<QuotesTable>[] = [
    {
      header: t("Quote ID"),
      accessorKey: "number",
      cell: (cell) => <IDMobile>{`${cell.getValue()}`}</IDMobile>,
    },
    {
      header: t("Product(s)"),
      accessorKey: "products",
      cell: (cell) => {
        return (
          <TooltipCell
            data-for={`${
              (cell.getValue() as { items: QuoteRequestItem[]; id: string }).id
            }-tooltip`}
            data-tip={""}
          >
            <TrimmedName
              text={getQuoteProductsAsText(
                (cell.getValue() as { items: QuoteRequestItem[]; id: string })
                  .items,
                t
              )}
            />
            <ReactTooltip
              id={`${
                (cell.getValue() as { items: QuoteRequestItem[]; id: string })
                  .id
              }-tooltip`}
              place="top"
              data-html={true}
              effect="solid"
              backgroundColor="#60676F"
              multiline={true}
            >
              {getQuoteItemsList(
                (cell.getValue() as { items: QuoteRequestItem[]; id: string })
                  .items
              )}
            </ReactTooltip>
          </TooltipCell>
        );
      },
    },
    {
      header: t("From"),
      accessorKey: "from",
    },
    {
      header: t("Total Value"),
      accessorKey: "total_value",
    },
    {
      header: t("Last Activity"),
      accessorKey: "last_activity",
    },
    {
      header: t("Status"),
      accessorKey: "status",
      size: 50,
      minSize: 50,
      cell: (cell) => (
        <SellerQuoteStatus
          status={(cell.getValue() as StatusAndId<QuoteStatus>).status}
          id={(cell.getValue() as StatusAndId<QuoteStatus>).id}
        />
      ),
    },
  ];
  const custom_columns: { [accessorKey: string]: number } =
    roleIsSomeKindOfSeller
      ? {
          account_manager_csm: 4,
          unread_message_count: 5,
        }
      : {
          unread_message_count: 5,
        };

  // Data transformation functions
  const dataTransformer = useCallback(
    (response: IQuotesAPIResponse): QuotesTable[] => {
      const getUnread = (quote: IQuoteRequest): boolean => {
        return notifications?.quotes.ids
          ? notifications?.quotes.ids.includes(quote?.id)
          : false;
      };

      return response.data.map((quote: IQuoteRequest) => ({
        id: quote.id,
        number: quote.number,
        products: { items: quote.items, id: quote.id },
        from: quote.buyer_name || "--",
        total_value: quote.items.length
          ? (() => {
              const { totalString } = calculatePriceDetails({
                items: quote.items,
                fees: quote.fees,
                totalDiscount: quote.total_discounts,
                currencyCode: quote.items[0].currency,
              });
              return totalString;
            })()
          : "-/-",
        last_activity: formatDateTime(quote.modified_at) || "--",
        status: { status: quote.status || "--", id: quote.id },
        unread: getUnread(quote),
        unread_message_count: quote.unread_message_count,
        account_manager_csm: getAccountManagerCSM(quote),
      }));
    },
    [notifications?.quotes.ids]
  );

  const paginationTransformer = useCallback(
    (response: IQuotesAPIResponse) => ({
      total: response.pagination.total,
      offset: response.pagination.offset,
    }),
    []
  );

  const getUnreadIds = useCallback((quote: QuotesTable) => quote.unread, []);

  // Use the shared list page hook
  const {
    state,
    data: tableData,
    isLoading,
    error,
    mutate,
    handlers,
    tableColumns,
  } = useListPage({
    defaultPerPage: DEFAULT_PER_PAGE,
    perPageOptions: [10, 20, 50, 100],
    endpoint: `/v1/storefronts/${storefront_id}/quotes`,
    statusTransformer: update_status_string_list,
    dataTransformer,
    paginationTransformer,
    getUnreadIds,
    notifications: notifications?.quotes,
    statusFilters: SellerQuoteStatusFilters(t),
    accountManagerFilters: accountManagers,
    customerServiceRepFilters: customerServiceReps,
    columns,
    custom_columns,
    basePath: `${accountPath}/quotes`,
    searchPlaceholder: getSearchPlaceholder(),
  });

  // Row click handler
  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    handlers.handleRowClick(e, e.currentTarget.id, `${accountPath}/quotes`);
  };

  return (
    <PageWrapper>
      <TablePageLayout
        showTitle
        title={localStrings(t).pageTitle}
        searchQuery={state.searchQuery}
        searchPlaceholder={getSearchPlaceholder()}
        onSearchChange={handlers.handleSearch}
        onSearchClear={handlers.handleClearSearch}
        toolbarActions={
          <>
            <SecondaryButtonLargeV2
              onClick={() => state.setDownloadReportSlideOut(true)}
            >
              {t("Download Report")}
            </SecondaryButtonLargeV2>
            <PrimaryButtonWithPlusIcon
              onClick={() => state.setShowCreateModal(true)}
              useV2
            >
              {t("Create New")}
            </PrimaryButtonWithPlusIcon>
          </>
        }
        filtersSlot={
          <>
            <FiltersDropDown
              activeItems={state.selectedStatus}
              applyStatus={handlers.applyStatus}
              list={SellerQuoteStatusFilters(t)}
            />
            {roleIsSomeKindOfSeller && accountManagers.length > 0 && (
              <FiltersDropDown
                activeItems={state.selectedAccountManagers}
                applyStatus={handlers.applyAccountManager}
                list={accountManagers}
                is_status={false}
                header={t("Account Manager")}
              />
            )}
            {roleIsSomeKindOfSeller && customerServiceReps.length > 0 && (
              <FiltersDropDown
                activeItems={state.selectedCustomerServiceReps}
                applyStatus={handlers.applyCustomerServiceRep}
                list={customerServiceReps}
                is_status={false}
                header={t("Customer Success Manager")}
              />
            )}
          </>
        }
        perPage={state.perPage}
        perPageItems={state.perPageItems}
        onPerPageChange={handlers.changePerPage}
        columns={tableColumns}
        data={tableData}
        isLoading={isLoading}
        error={error}
        rowClick={handleRowClick}
        rowHover={rowHover}
        pagination={state.tablePagination}
        totalCount={state.tablePagination.totalCount}
        offset={state.offset}
        onPageChange={handlers.changePage}
      >
        <Modal
          show={state.showCreateModal}
          closeModal={() => state.setShowDialogBox(true)}
          modalWidth={"95%"}
        >
          <SellerCreateQuote
            onSuccess={() => {
              state.setShowCreateModal(false);
              mutate();
            }}
          />
        </Modal>

        <ConfirmDialog
          show={state.showDialogBox}
          confirmMessage={t("Are you sure you want to cancel this quote?")}
          closeDialog={() => state.setShowDialogBox(false)}
          handleConfirm={() => {
            state.setShowCreateModal(false);
            state.setShowDialogBox(false);
          }}
        />

        <SlideOut
          closeFlyout={handlers.closeDownloadReportSlideout}
          show={state.downloadReportSlideOut}
          title={t("Download Quote Report")}
        >
          <DownloadReport
            closeSlideout={handlers.closeDownloadReportSlideout}
            transactionType="quote"
          />
        </SlideOut>
      </TablePageLayout>
    </PageWrapper>
  );
};
